import React, { useState } from 'react';
import { Drawer } from '@mui/material';
import {
  ArrowTopRightOnSquareIcon,
  CalendarDaysIcon,
  ClockIcon,
  MegaphoneIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { Appointment, ChurchEvent } from '../../types';
import { t } from '../../utils/translations';
import {
  formatCalendarDate,
  formatDurationLabel,
  formatTimeRange,
  isDateTodayOrFuture,
  isToday,
  getRoomColorStyles,
  sortAppointmentsByTime,
} from './calendarUtils';
import {
  sortEventsByTime,
  getEventDisplayStyles,
  formatEventDateRange,
  formatEventTimeRange,
} from './eventUtils';
import ConfirmDialog from '../common/ConfirmDialog';
import ExpandCollapse from '../common/ExpandCollapse';
import Button, { IconButton } from '../ui/Button';
import RowActions from '../ui/RowActions';

interface CalendarDayDialogProps {
  selectedDate: Date | null;
  appointments: Appointment[];
  events: ChurchEvent[];
  /** Desktop: embedded panel. Mobile: slide-over drawer. */
  variant?: 'panel' | 'drawer';
  /** Drawer only: whether open */
  open?: boolean;
  onClose?: () => void;
  onEditAppointment: (appointment: Appointment) => void;
  onDeleteAppointment: (appointmentId: string) => void | Promise<void>;
  onScheduleAppointment: (date: Date) => void;
  onEditEvent: (event: ChurchEvent) => void;
  onDeleteEvent: (eventId: string) => void | Promise<void>;
  onAddEvent: (date: Date) => void;
  canEdit: (appointment: Appointment) => boolean;
  canDelete: (appointment: Appointment) => boolean;
  canManageEvents: boolean;
  canViewCaseDetails?: (appointment: Appointment) => boolean;
}

type DeleteTarget =
  | { kind: 'appointment'; item: Appointment }
  | { kind: 'event'; item: ChurchEvent };

function daySummaryLabel(appointmentCount: number, eventCount: number): string {
  if (appointmentCount === 0 && eventCount === 0) {
    return t.appointments.noAppointmentsDay;
  }
  const parts: string[] = [];
  if (appointmentCount > 0) {
    parts.push(
      `${appointmentCount} ${appointmentCount === 1 ? 'sesiune' : 'sesiuni'}`
    );
  }
  if (eventCount > 0) {
    parts.push(
      `${eventCount} ${eventCount === 1 ? 'eveniment' : 'evenimente'}`
    );
  }
  return parts.join(' · ');
}

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  return parts
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
}

function DayItemCard({
  accent,
  timeRange,
  duration,
  title,
  tagLabel,
  personName,
  secondaryLabel,
  actions,
  extra,
  titleExpanded,
  onToggleTitle,
}: {
  accent: string;
  timeRange: string;
  duration?: string;
  title: string;
  tagLabel?: string;
  personName?: string;
  secondaryLabel?: string;
  actions?: React.ReactNode;
  extra?: React.ReactNode;
  titleExpanded?: boolean;
  onToggleTitle?: () => void;
}) {
  const titleNeedsExpand = title.length > 72;
  const titlePreview = titleNeedsExpand ? title.slice(0, 72).trimEnd() : title;
  const titleRest = titleNeedsExpand ? title.slice(titlePreview.length) : '';
  const hasMeta = Boolean(tagLabel || personName || secondaryLabel);

  return (
    <article className="relative overflow-hidden rounded-xl border border-slate-200 bg-white py-3 pl-4 pr-3 transition hover:border-slate-300 hover:shadow-sm">
      <span
        className="absolute inset-y-0 left-0 w-[3.5px]"
        style={{ backgroundColor: accent }}
        aria-hidden="true"
      />
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-1.5 text-xs font-semibold text-slate-900">
            <ClockIcon className="h-3.5 w-3.5 shrink-0 text-slate-400" />
            <span>{timeRange}</span>
            {duration ? <span className="font-medium text-slate-400">· {duration}</span> : null}
          </div>
          <div className="mt-1.5 break-words text-sm font-semibold leading-snug text-slate-900">
            {titlePreview}
            {titleNeedsExpand && !titleExpanded ? '…' : null}
            <ExpandCollapse open={Boolean(titleExpanded)}>{titleRest}</ExpandCollapse>
          </div>
          {titleNeedsExpand && onToggleTitle ? (
            <button
              type="button"
              onClick={onToggleTitle}
              className="mt-0.5 text-xs font-semibold text-brand-600 transition hover:text-brand-700"
            >
              {titleExpanded ? t.common.showLess : t.common.showMore}
            </button>
          ) : null}
        </div>
        {actions}
      </div>

      {hasMeta ? (
        <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1.5">
          {tagLabel ? (
            <span
              className="inline-flex max-w-full items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-semibold"
              style={{ color: accent, backgroundColor: `${accent}1f` }}
            >
              <span
                className="h-1.5 w-1.5 shrink-0 rounded-full"
                style={{ backgroundColor: accent }}
              />
              <span className="truncate">{tagLabel}</span>
            </span>
          ) : null}
          {personName ? (
            <span className="inline-flex min-w-0 items-center gap-1.5 text-xs text-slate-500">
              <span className="grid h-[18px] w-[18px] shrink-0 place-items-center rounded-full bg-slate-100 text-[8px] font-bold text-slate-500">
                {getInitials(personName)}
              </span>
              <span className="truncate">{personName}</span>
            </span>
          ) : null}
          {secondaryLabel ? (
            <span className="inline-flex min-w-0 items-center gap-1.5 text-xs text-slate-500">
              <CalendarDaysIcon className="h-3.5 w-3.5 shrink-0 text-slate-400" />
              <span className="truncate">{secondaryLabel}</span>
            </span>
          ) : null}
        </div>
      ) : null}

      {extra ? (
        <div className="mt-3 border-t border-dashed border-slate-200 pt-3">{extra}</div>
      ) : null}
    </article>
  );
}

function ExpandableBody({
  text,
  expanded,
  onToggle,
  threshold = 160,
}: {
  text: string;
  expanded: boolean;
  onToggle: () => void;
  threshold?: number;
}) {
  const needsExpand = text.length > threshold;
  const preview = needsExpand ? text.slice(0, threshold).trimEnd() : text;
  const rest = needsExpand ? text.slice(preview.length) : '';
  return (
    <div>
      <div className="whitespace-pre-wrap break-words text-[13px] leading-relaxed text-slate-600">
        {preview}
        {needsExpand && !expanded ? '…' : null}
        <ExpandCollapse open={needsExpand && expanded}>{rest}</ExpandCollapse>
      </div>
      {needsExpand ? (
        <button
          type="button"
          onClick={onToggle}
          className="mt-1 text-xs font-semibold text-brand-600 transition hover:text-brand-700"
        >
          {expanded ? t.common.showLess : t.common.showMore}
        </button>
      ) : null}
    </div>
  );
}

const CalendarDayDialog: React.FC<CalendarDayDialogProps> = ({
  selectedDate,
  appointments,
  events,
  variant = 'panel',
  open = false,
  onClose,
  onEditAppointment,
  onDeleteAppointment,
  onScheduleAppointment,
  onEditEvent,
  onDeleteEvent,
  onAddEvent,
  canEdit,
  canDelete,
  canManageEvents,
  canViewCaseDetails = () => false,
}) => {
  const isDrawer = variant === 'drawer';
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [localAppointments, setLocalAppointments] = useState(appointments);
  const [localEvents, setLocalEvents] = useState(events);
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(() => new Set());

  React.useEffect(() => {
    if (deleteLoading) return;
    setLocalAppointments(appointments);
    setLocalEvents(events);
  }, [appointments, events, deleteLoading]);

  React.useEffect(() => {
    setExpandedKeys(new Set());
  }, [selectedDate]);

  const toggleExpanded = (key: string) => {
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const sortedAppointments = sortAppointmentsByTime(localAppointments);
  const sortedEvents = sortEventsByTime(localEvents);
  const eventStyles = getEventDisplayStyles();
  const isEmpty = sortedAppointments.length === 0 && sortedEvents.length === 0;
  const showCreateActions = Boolean(selectedDate && isDateTodayOrFuture(selectedDate));

  const handleDeleteConfirm = async () => {
    if (!deleteTarget || deleteLoading) return;
    const target = deleteTarget;
    if (target.kind === 'appointment' && !canEdit(target.item) && !canDelete(target.item)) {
      return;
    }
    // Events: leaders/admins may delete past events too.
    try {
      setDeleteLoading(true);
      if (target.kind === 'appointment') {
        await onDeleteAppointment(target.item.id);
        setLocalAppointments((prev) => prev.filter((a) => a.id !== target.item.id));
      } else {
        await onDeleteEvent(target.item.id);
        setLocalEvents((prev) => prev.filter((e) => e.id !== target.item.id));
      }
      setDeleteTarget(null);
    } catch {
      // Parent shows snackbar; keep confirm open so the user can retry or cancel
    } finally {
      setDeleteLoading(false);
    }
  };

  const dayContent = (
    <>
      <div className="shrink-0 border-b border-slate-200 px-4 pb-4 pt-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-base font-semibold text-slate-900 first-letter:uppercase">
              {selectedDate ? formatCalendarDate(selectedDate) : '—'}
            </h2>
            <p className="mt-0.5 text-sm text-slate-500">
              {daySummaryLabel(sortedAppointments.length, sortedEvents.length)}
              {selectedDate && isToday(selectedDate) ? ` · ${t.dashboard.today}` : ''}
            </p>
          </div>
          {isDrawer && onClose && (
            <IconButton
              icon={XMarkIcon}
              size="md"
              label={t.common.close}
              onClick={onClose}
            />
          )}
        </div>

        {showCreateActions && selectedDate && (
          <div className="mt-4 flex flex-wrap gap-2">
            <Button
              variant="primary"
              icon={CalendarDaysIcon}
              onClick={() => onScheduleAppointment(selectedDate)}
              className="min-w-[9rem] flex-1"
            >
              {t.appointments.schedule}
            </Button>
            {canManageEvents && (
              <Button
                icon={MegaphoneIcon}
                onClick={() => onAddEvent(selectedDate)}
                className="min-w-[9rem] flex-1"
              >
                {t.events.singular}
              </Button>
            )}
          </div>
        )}
      </div>

      <div className="flex-1 overflow-auto px-4 py-4">
        {isEmpty ? (
          <p className="py-8 text-center text-sm text-slate-500">
            {t.appointments.noAppointmentsDay}
          </p>
        ) : (
          <div className="space-y-2.5">
            <div className="flex items-center gap-2.5">
              <span className="text-[10.5px] font-bold uppercase tracking-[0.08em] text-slate-400">
                {t.appointments.dayScheduleLabel}
              </span>
              <span className="h-px flex-1 bg-slate-200" />
            </div>

            {sortedAppointments.map((appointment) => {
              const roomColors = getRoomColorStyles(appointment.room);
              const canSeeCase = canViewCaseDetails(appointment);
              const title = canSeeCase
                ? appointment.caseTitle || appointment.title
                : appointment.counselorName || appointment.title;
              const showDescription = canSeeCase && Boolean(appointment.description);
              const titleKey = `appt-title-${appointment.id}`;
              const descKey = `appt-desc-${appointment.id}`;

              return (
                <DayItemCard
                  key={`appt-${appointment.id}`}
                  accent={roomColors.accent}
                  timeRange={formatTimeRange(appointment.startTime, appointment.endTime)}
                  duration={formatDurationLabel(appointment.startTime, appointment.endTime)}
                  title={title}
                  tagLabel={appointment.room || undefined}
                  personName={canSeeCase ? appointment.counselorName || undefined : undefined}
                  titleExpanded={expandedKeys.has(titleKey)}
                  onToggleTitle={() => toggleExpanded(titleKey)}
                  actions={
                    <RowActions
                      onEdit={
                        canEdit(appointment) ? () => onEditAppointment(appointment) : undefined
                      }
                      onDelete={
                        canDelete(appointment)
                          ? () => setDeleteTarget({ kind: 'appointment', item: appointment })
                          : undefined
                      }
                    />
                  }
                  extra={
                    showDescription && appointment.description ? (
                      <ExpandableBody
                        text={appointment.description}
                        expanded={expandedKeys.has(descKey)}
                        onToggle={() => toggleExpanded(descKey)}
                      />
                    ) : undefined
                  }
                />
              );
            })}

            {sortedEvents.map((event) => {
              const hasDetails = Boolean(event.description || event.registrationUrl);
              // Leaders/admins can always edit or delete events (including past ones).
              const canModifyEvent = canManageEvents;
              const titleKey = `event-title-${event.id}`;
              const descKey = `event-desc-${event.id}`;

              return (
                <DayItemCard
                  key={`event-${event.id}`}
                  accent={eventStyles.accent}
                  timeRange={formatEventTimeRange(event)}
                  duration={formatDurationLabel(event.startTime, event.endTime)}
                  title={event.name}
                  tagLabel={t.events.singular}
                  secondaryLabel={formatEventDateRange(event)}
                  titleExpanded={expandedKeys.has(titleKey)}
                  onToggleTitle={() => toggleExpanded(titleKey)}
                  actions={
                    <RowActions
                      onEdit={canModifyEvent ? () => onEditEvent(event) : undefined}
                      onDelete={
                        canModifyEvent
                          ? () => setDeleteTarget({ kind: 'event', item: event })
                          : undefined
                      }
                    />
                  }
                  extra={
                    hasDetails ? (
                      <div className="space-y-2">
                        {event.description ? (
                          <ExpandableBody
                            text={event.description}
                            expanded={expandedKeys.has(descKey)}
                            onToggle={() => toggleExpanded(descKey)}
                          />
                        ) : null}
                        {event.registrationUrl ? (
                          <>
                            <p className="rounded-lg bg-amber-50 px-2.5 py-1.5 text-xs text-amber-800">
                              {t.events.registrationMandatory}
                            </p>
                            <a
                              href={event.registrationUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1.5 text-xs font-medium text-brand-700 hover:underline"
                            >
                              <ArrowTopRightOnSquareIcon
                                className="h-4 w-4 shrink-0"
                                aria-hidden="true"
                              />
                              {t.events.registrationLink}
                            </a>
                          </>
                        ) : null}
                      </div>
                    ) : undefined
                  }
                />
              );
            })}
          </div>
        )}
      </div>
    </>
  );

  const confirmDialog = (
    <ConfirmDialog
      open={deleteTarget !== null}
      title={
        deleteTarget?.kind === 'event'
          ? t.events.deleteEvent
          : t.appointments.deleteAppointment
      }
      message={
        deleteTarget?.kind === 'event'
          ? t.events.deleteConfirm.replace('{name}', deleteTarget.item.name)
          : t.deleteWarnings.deleteAppointmentConfirm.replace(
              '{title}',
              deleteTarget?.kind === 'appointment' ? deleteTarget.item.title : ''
            )
      }
      variant="danger"
      loading={deleteLoading}
      onClose={() => {
        if (!deleteLoading) setDeleteTarget(null);
      }}
      onConfirm={handleDeleteConfirm}
    />
  );

  if (isDrawer) {
    return (
      <>
        <Drawer
          anchor="right"
          open={open}
          onClose={onClose}
          PaperProps={{
            sx: {
              width: '100%',
              maxWidth: 420,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              bgcolor: '#fff',
            },
          }}
        >
          {dayContent}
        </Drawer>
        {confirmDialog}
      </>
    );
  }

  return (
    <>
      <aside className="flex h-full min-h-[28rem] flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        {dayContent}
      </aside>
      {confirmDialog}
    </>
  );
};

export default CalendarDayDialog;
